/*global $:false */

import { DEV_MODE } from '@cob/ui-genesis/helpers/cob.devmode.js';
import IndexedInstanceM from './recordm.model.entity.indexedinstance.js';

import utils from '@cob/ui-genesis/cob.utils.js';

//Model responsible for executing all searches
var SearchM = function(core, searchUrl, downloadUrl) {
    this._core = core;

    this.searchurl = searchUrl;
    this.downloadUrl = downloadUrl + "?";

    this._rowsPerPage = 30;
    this._query = undefined;
    this._filter = undefined;
    this._from = 0;
    this._sortField = undefined;
    this._defaultSortField = undefined;
    this._sortAsc = false;
    this._defaultSortAsc = false;

    this._totalMatchingInstances = 0;
    this._searchResults = [];
    this._definitions = [];
};

SearchM.prototype.doSearch = function(query, from, sortField, sortAsc, eventId, size) {
    this.reset();

    this._query = query;
    this._from = from || 0;
    this._sortField = sortField || this._sortField;

    if(sortAsc !== undefined){
        this._sortAsc = sortAsc;
    }

    this._executeSearch({eventId: eventId, size: size});
};

SearchM.prototype.loadMoreData = function() {
    // we'll start **after** this page
    this._from += this._rowsPerPage;

    this._executeSearch();
};

SearchM.prototype.refresh = function() {
    this._executeSearch({refresh: true});
};

SearchM.prototype.count = function() {
    $.ajax({
        url: this.searchurl + "/count?",
        type: 'GET',
        contentType: "application/json",
        success: function(data) {
            this._core.publish('recordm:search:count-finished', data);
        },
        error: function(req) {
            window.console.log("SEARCH-COUNT:ERROR: " + req.status + " : " + req.statusText + ":" + req.responseText);
        }
    });
};

SearchM.prototype.reset = function() {
    this._resetModel();
};

SearchM.prototype.getSearchResults = function() {
    return this._searchResults;
};

SearchM.prototype.sort = function(sortField, sortAsc) {
    this._sortField = sortField;
    this._sortAsc = sortAsc;

    if (this._totalMatchingInstances > 0) {
        this.refresh();
    }
};

SearchM.prototype.isSorted = function() {
    return this._sortField !== undefined;
};

SearchM.prototype.getSortField = function() {
    return this._sortField;
};

SearchM.prototype.isSortAscending = function() {
    return this._sortAsc || false;
};

SearchM.prototype.setDefaultSort = function(newSortField, newSortAsc) {
    this._defaultSortField = newSortField || this._defaultSortField;
    this._defaultSortAsc = newSortAsc || this._defaultSortAsc;
};


SearchM.prototype.setRowsPerPage = function(number) {
    this._rowsPerPage = number;
};

SearchM.prototype.getRowsPerPage = function() {
    return this._rowsPerPage;
};

SearchM.prototype.getTotalMatchingResults = function() {
    return this._totalMatchingInstances;
};

SearchM.prototype.getDefinitions = function() {
    if (this._definitions) {
        this._definitions.sort(function(me, other) {
            return me.name.localeCompare(other.name);
        });
    }

    return this._definitions;
};

SearchM.prototype.getQuery = function() {
    return this._query
};

SearchM.prototype.setCurrentQuery = function(query) {
    this._query = query;
};

SearchM.prototype.getCurrentQuery = function() {
    let processedQuery;

    if (this._filter) {
        processedQuery = this._filter;
        if (this._query) {
            processedQuery += " AND (" + this._query + ")";
        }
    } else {
        processedQuery = this._query;
    }

    DEV_MODE && console.debug("Filtered Query: ", processedQuery)

    return processedQuery;

};

SearchM.prototype.setCurrentFilter = function(query) {
    this._filter = query;
};

SearchM.prototype.getCurrentFilter = function() {
    return this._filter;
};

SearchM.prototype.getExportURL = function(selectedIds, visibleColumnsNames, visibleColumnsLabels) {
    var query = this.getCurrentQuery() || "*",
        url = this.downloadUrl;

    if (selectedIds && selectedIds.length > 0) {
        query = selectedIds.map(function(id) {
            //By default all records have the id column as the identifier.
            return "id:" + id;
        }).join(" OR ");
    }

    url += 'vc=' + encodeURIComponent(visibleColumnsNames);
    url += '&vcn=' + encodeURIComponent(visibleColumnsLabels);

    if (this.isSorted()) {
        url += '&sort=' + encodeURIComponent(this.getSortField());
        url += '&ascending=' + this.isSortAscending().toString();
    }

    url += `&tz=${Intl.DateTimeFormat().resolvedOptions().timeZone}`;

    return url + "&q=" + encodeURIComponent(query);
};

SearchM.prototype.removeFromSearchResult = function(indexedInstanceM) {
    this._searchResults = this._searchResults.filter(function(resultEntity) {
        return !(resultEntity.entityKey === indexedInstanceM.getEntityKey()
            && resultEntity.id === indexedInstanceM.getId());
    });

    // We have one less id so we decrement the total of the definition
    this._definitions.forEach(function(definition) {
        if (definition.id === indexedInstanceM.data.definitionId) {
            definition.total--;
        }
    });

    this._totalMatchingInstances--;
    this._from--;
};

// ## Private Helpers

// Private helper that resets the search
SearchM.prototype._resetModel = function() {
    this._query = undefined;
    this._from = 0;
    this._sortField = this._defaultSortField;
    this._sortAsc = this._defaultSortAsc;

    this._clearSearchResults();
};

// Clear any preious search results without clearing the query.
// Useful when you want to make a refresh
SearchM.prototype._clearSearchResults = function() {
    this._searchResults = [];
    this._definitions = [];
    this._totalMatchingInstances = 0;
};

//Private helper that stores a list of entities ids
SearchM.prototype._storeResults = function(data) {
    var _this = this;

    // Reset the definitions.
    // This is beeing rendered on every search request
    this._definitions = [];

    // update the total records matching
    this._totalMatchingInstances = data.hits.total.value;

    utils.forEachOwn(data._definitions, function(definitionId) {
        var definition = data._definitions[definitionId];

        var definitionInfo = {
            id: definition.id,
            name: definition.name,
            total: definition.total
        };

        // Update the model `_definitions` variable
        _this._definitions.push(definitionInfo);
    });

    // update the data with the new data
    data.hits.hits.forEach(function(hit) {
        _this._storeOne(new IndexedInstanceM(_this._core, hit._source))
    });
};

//Private helper that stores and subscribes to a single entity
SearchM.prototype._storeOne = function(indexedInstanceM) {
    const modelKey = indexedInstanceM.getEntityKey() + ":" + indexedInstanceM.getId();
    this._core.addModel(modelKey, indexedInstanceM);
    this._searchResults.push({modelKey: modelKey, entityKey: indexedInstanceM.getEntityKey(), id: indexedInstanceM.getId()});
};

export default SearchM;
